/**
 * Hook para consultar catálogo de erros de envio de mensagens
 * 
 * Busca dados do banco via React Query com cache de 30 min.
 * Expõe getErrorInfo() para obter título, explicação e sugestão.
 * Inclui fallback para errorMap hardcoded durante transição.
 * 
 * @release v1.0.0 - 2026-02-28
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { extractEmbeddedErrorCode } from '@/lib/errorMetadata';

// Logger condicional
const logger = createLogger('useErrorCatalog');

/**
 * Estrutura de um erro catalogado retornado pelo banco
 */
export interface CatalogError {
  id: string;
  error_code: string;
  provider: string;
  title: string;
  explanation: string;
  suggestion: string | null;
  category: string | null;
  severity: string;
  icon_name: string | null;
  icon_color: string | null;
  match_keywords: string[] | null;
  is_cataloged: boolean;
}

/**
 * Informações do erro para exibição no frontend
 */
export interface ErrorInfo {
  title: string;
  explanation: string;
  suggestion: string;
  category: string;
  severity: string;
  icon_name: string | null;
  icon_color: string | null;
  is_cataloged: boolean;
  match_keywords: string[] | null;
}

/**
 * Busca o catálogo completo de erros do banco
 */
const fetchErrorCatalog = async (): Promise<CatalogError[]> => {
  const { data, error } = await supabase
    .from('message_error_catalog')
    .select('id, error_code, provider, title, explanation, suggestion, category, severity, icon_name, icon_color, match_keywords, is_cataloged')
    .order('error_code');

  if (error) {
    logger.error('Erro ao buscar catálogo de erros:', error);
    return [];
  }

  return (data || []) as CatalogError[];
};

/**
 * Hook principal para acessar o catálogo de erros
 * 
 * @example
 * const { getErrorInfo, isLoading } = useErrorCatalog();
 * const info = getErrorInfo('132001', 'meta_waba');
 */
export function useErrorCatalog() {
  const { data: catalog = [], isLoading } = useQuery({
    queryKey: ['message-error-catalog'],
    queryFn: fetchErrorCatalog,
    staleTime: 30 * 60 * 1000, // 30 minutos - erros mudam raramente
    gcTime: 60 * 60 * 1000, // 1 hora no garbage collector
    refetchOnWindowFocus: false,
  });

  /**
   * Busca informações do erro no catálogo
   * Prioriza match exato por provider, depois fallback para 'all'
   * 
   * Para código 131042 (ambíguo), usa match_keywords para detectar subtipo
   * baseado no conteúdo da mensagem original
   * 
   * @param code - Código do erro
   * @param provider - Slug do provider (ex: 'meta_waba', 'z-api')
   * @param originalMessage - Mensagem original para matching inteligente
   * @param originalTitle - Título original para matching inteligente
   */
  const getErrorInfo = (
    code: string | number | undefined,
    provider?: string | null,
    originalMessage?: string,
    originalTitle?: string
  ): ErrorInfo | null => {
    if (!code) return null;
    const codeStr = String(code);

    // Busca match exato por provider primeiro, depois 'all'
    let match = catalog.find(
      e => e.error_code === codeStr && e.provider === provider
    );
    if (!match) {
      match = catalog.find(
        e => e.error_code === codeStr && e.provider === 'all'
      );
    }

    if (!match) return null;

    // Matching inteligente via match_keywords para códigos ambíguos (ex: 131042)
    if (match.match_keywords && match.match_keywords.length > 0 && (originalMessage || originalTitle)) {
      const combinedText = `${originalMessage || ''} ${originalTitle || ''}`.toLowerCase();

      // Verifica se alguma keyword bate para refinar o subtipo
      // Busca variantes específicas no catálogo
      if (combinedText.includes('payment')) {
        const paymentMatch = catalog.find(e => e.error_code === `${codeStr}_PAYMENT`);
        if (paymentMatch) return formatCatalogEntry(paymentMatch);
      }
      if (combinedText.includes('suspended') || combinedText.includes('locked')) {
        const suspendedMatch = catalog.find(e => e.error_code === `${codeStr}_SUSPENDED`);
        if (suspendedMatch) return formatCatalogEntry(suspendedMatch);
      }
      if (combinedText.includes('blocked') || combinedText.includes('not eligible')) {
        const blockedMatch = catalog.find(e => e.error_code === `${codeStr}_BLOCKED`);
        if (blockedMatch) return formatCatalogEntry(blockedMatch);
      }
    }

    return formatCatalogEntry(match);
  };

  return {
    catalog,
    isLoading,
    getErrorInfo,
  };
}

/**
 * Formata entrada do catálogo para exibição
 */
function formatCatalogEntry(entry: CatalogError): ErrorInfo {
  return {
    title: entry.title,
    explanation: entry.explanation,
    suggestion: entry.suggestion || 'Verifique os detalhes técnicos e contate o suporte se necessário.',
    category: entry.category || 'Geral',
    severity: entry.severity,
    icon_name: entry.icon_name,
    icon_color: entry.icon_color,
    is_cataloged: entry.is_cataloged,
    match_keywords: entry.match_keywords,
  };
}

/**
 * Registra erro de envio no catálogo de forma assíncrona (fire-and-forget)
 * Não bloqueia a UX - erros no registro são logados mas ignorados
 * 
 * @param params - Dados do erro para registro
 */
export async function registerSendError(params: {
  errorCode: string;
  provider: string;
  tenantId?: string;
  inboxId?: string;
  messageId?: string;
  conversationId?: string;
  messageType?: string;
  rawError?: unknown;
  providerMessage?: string;
}) {
  try {
    // Normalização: se código é genérico (500/503/UNKNOWN), tenta extrair código real
    // do texto da mensagem do provider (ex: "[132001] Template not found")
    const GENERIC_CODES = ['500', '503', 'UNKNOWN'];
    let finalCode = params.errorCode;
    
    if (GENERIC_CODES.includes(finalCode)) {
      const rawErrorStr = params.rawError ? JSON.stringify(params.rawError) : '';
      const extracted = extractEmbeddedErrorCode(params.providerMessage) 
        || extractEmbeddedErrorCode(rawErrorStr);
      if (extracted) {
        logger.info(`Código normalizado de ${finalCode} para ${extracted}`);
        finalCode = extracted;
      }
    }

    await supabase.rpc('register_send_error', {
      p_error_code: finalCode,
      p_provider: params.provider || 'unknown',
      p_inbox_id: params.inboxId || null,
      p_message_id: params.messageId || null,
      p_conversation_id: params.conversationId || null,
      p_message_type: params.messageType || null,
      p_raw_error: params.rawError ? JSON.parse(JSON.stringify(params.rawError)) : null,
      p_provider_message: params.providerMessage || null,
    });
  } catch (err) {
    // Fire-and-forget: não bloqueia UX, apenas loga
    logger.warn('Falha ao registrar erro no catálogo (fire-and-forget):', err);
  }
}
